import type { BadgeTone } from '@/components/ui/Badge';
import { addDays, fromISO, todayISO } from '@/features/institute/calendar/utils/dates';
import { ACTIVITY_TYPE_LABEL } from '@/features/teacher/activities/constants';
import type {
  ParentTaskPriority,
  ParentWorkItem,
  ParentWorkState,
  ParentWorkType,
} from './types/parent';

/**
 * Reading a piece of work's standing off its dates, in one place.
 *
 * Every figure on the Homework & Tasks screen — the five tiles, the four filter
 * chips, the label on a row and the badge over the details — asks the same
 * question, so all of them ask it here. Two copies of this rule is a tile
 * counting one thing and the row beside it saying another.
 */

/**
 * Today as `YYYY-MM-DD` in the school's timezone — the same day the server means.
 *
 * The arithmetic itself — today, local midnight, adding days, the month grid —
 * is the calendar's `utils/dates`, which had all of it already and had thought
 * about the parts that go wrong (a bare `new Date('2026-05-01')` is UTC
 * midnight, and stepping months without clamping skips March). This is the
 * panel's own name for it rather than a second implementation (CLAUDE.md §3).
 */
export function todayIso(): string {
  return todayISO();
}

/** How far ahead "due this week" reaches. */
const WEEK_DAYS = 7;

/**
 * Where one piece of work stands today.
 *
 * Handed in is finished, whatever the date says — work marked late is still
 * work done. After that the due date decides, and anything further out than a
 * week is upcoming rather than pending, so a parent's "4 pending" means four
 * things to see to now and not everything set this term.
 */
export function workState(item: ParentWorkItem, today: string): ParentWorkState {
  if (item.submittedAt) return 'COMPLETED';
  if (item.dueDate < today) return 'OVERDUE';
  if (item.dueDate <= addDays(today, WEEK_DAYS)) return 'PENDING';

  return 'UPCOMING';
}

/** Due within the next seven days and not yet handed in. */
export function isDueThisWeek(item: ParentWorkItem, today: string): boolean {
  return !item.submittedAt && item.dueDate >= today && item.dueDate <= addDays(today, WEEK_DAYS);
}

/** Set in the calendar month the reader is in. What "This month" counts. */
export function isAssignedThisMonth(item: ParentWorkItem, today: string): boolean {
  return item.assignedDate.slice(0, 7) === today.slice(0, 7);
}

export const WORK_STATE_LABEL: Record<ParentWorkState, string> = {
  COMPLETED: 'Completed',
  OVERDUE: 'Overdue',
  PENDING: 'Pending',
  UPCOMING: 'Upcoming',
};

/**
 * The product's status colours, unchanged.
 *
 * Overdue is red and done is green on every screen a school sees, so these are
 * not re-tinted to the panel's indigo — a status that means something different
 * from room to room stops being read as a status at all.
 */
export const WORK_STATE_TONE: Record<ParentWorkState, BadgeTone> = {
  COMPLETED: 'success',
  OVERDUE: 'critical',
  PENDING: 'warning',
  UPCOMING: 'neutral',
};

export const TASK_PRIORITY_LABEL: Record<ParentTaskPriority, string> = {
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
};

/**
 * Priority wears the status colours too, and for the same reason: a parent
 * scanning a table reads red as "this one first" without stopping to read the
 * word. Low is deliberately the quiet green rather than grey — it is a real
 * answer, not a missing one.
 */
export const TASK_PRIORITY_TONE: Record<ParentTaskPriority, BadgeTone> = {
  HIGH: 'critical',
  MEDIUM: 'warning',
  LOW: 'success',
};

/** Most pressing first, for sorting a table by priority. */
export const TASK_PRIORITY_ORDER: Record<ParentTaskPriority, number> = {
  HIGH: 0,
  MEDIUM: 1,
  LOW: 2,
};

/**
 * How long is left, in the words a parent would use.
 *
 * Whole days between two dates, not hours: work is due on a day, so "1 day
 * left" has to mean tomorrow whether it is read at breakfast or at midnight.
 */
export function daysLeftLabel(dueDate: string, today: string): string {
  const days = Math.round(
    (fromISO(dueDate).getTime() - fromISO(today).getTime()) / (24 * 60 * 60 * 1000),
  );

  if (days < -1) return `${Math.abs(days)} days late`;
  if (days === -1) return 'Due yesterday';
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';

  return `${days} days left`;
}

/** The day a date falls on, as the rail's deadline lines print it. */
export function formatShortDay(dateIso: string): string {
  return `${new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(fromISO(dateIso))} (${new Intl.DateTimeFormat('en-IN', {
    weekday: 'short',
  }).format(fromISO(dateIso))})`;
}

/**
 * Homework, and every kind of activity in the words the teacher's console uses
 * for it — the console's own labels, so a "Lab practical" set there is a "Lab
 * practical" here (CLAUDE.md §3).
 */
export const WORK_TYPE_LABEL: Record<ParentWorkType, string> = {
  HOMEWORK: 'Homework',
  ...ACTIVITY_TYPE_LABEL,
};

/** `2026-05-28` → `28 May 2026, Thu`, which is how a due date is read aloud. */
export function formatDueDay(dateIso: string): string {
  const date = fromISO(dateIso);
  if (Number.isNaN(date.getTime())) return '—';

  const day = new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
  const weekday = new Intl.DateTimeFormat('en-IN', { weekday: 'short' }).format(date);

  return `${day}, ${weekday}`;
}

/** `Monday, 26 May 2026` — the line that dates the screen. */
export function formatLongDay(dateIso: string): string {
  const date = fromISO(dateIso);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat('en-IN', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

/**
 * `27 May 2026, Wednesday at 08:45 PM` — when the work was handed in.
 *
 * A bare `YYYY-MM-DD` is printed without a time rather than with one invented:
 * `homework_submissions` records the day a hand-in was accepted, and rendering
 * that through a timezone would tell a parent their child submitted at 5:30 in
 * the morning. Anything carrying a real instant still prints it.
 */
export function formatSubmittedAt(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return formatDueDay(value);

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  const day = new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    weekday: 'long',
  }).format(date);
  const time = new Intl.DateTimeFormat('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(date);

  return `${day} at ${time.toUpperCase()}`;
}

/** `184320` → `180 KB`. The same rounding `FileUpload` shows on the way in. */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
